import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ShieldAlert, RefreshCw, FileSpreadsheet, Scale } from "lucide-react";

const TONE = { high: "destructive", medium: "default", low: "secondary" };

/** Admin → Operations: AML/surveillance cases, reconciliation and the finance export. */
export const AdminOps = () => {
  const [alerts, setAlerts] = useState([]);
  const [byRule, setByRule] = useState({});
  const [recon, setRecon] = useState(null);
  const [exp, setExp] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/foundation/compliance/alerts?status=open");
    setAlerts(data.alerts); setByRule(data.by_rule);
  }, []);
  useEffect(() => { load(); }, [load]);

  const resolve = async (id, resolution) => {
    await api.post(`/foundation/compliance/alerts/${id}/resolve`, { resolution, note: "" });
    toast.success(`Case ${resolution}`);
    load();
  };

  const runRecon = async () => {
    setBusy("recon");
    try {
      const { data } = await api.post("/foundation/treasury/reconciliation/run");
      setRecon(data);
      toast[data.clean ? "success" : "warning"](
        data.clean ? "Books reconcile" : `${data.total_breaks} breaks found`,
        { description: `${data.accounts} accounts checked.` });
    } finally { setBusy(""); }
  };

  const runExport = async () => {
    setBusy("export");
    try {
      const { data } = await api.get("/foundation/treasury/export");
      setExp(data);
      toast[data.balanced ? "success" : "error"](
        data.balanced ? "Journal balances" : "Journal does not balance",
        { description: `${data.row_count} postings, debits ${data.total_debits.toLocaleString()} vs credits ${data.total_credits.toLocaleString()}.` });
    } finally { setBusy(""); }
  };

  const openReport = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/foundation/compliance/reports/regulatory`, "_blank", "noopener");
  };

  return (
    <div className="grid gap-4 lg:grid-cols-3" data-testid="admin-ops">
      <Card className="p-5 shadow-premium lg:col-span-2" data-testid="admin-ops-alerts">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            <ShieldAlert className="h-4 w-4 text-destructive" />Open AML &amp; surveillance cases
          </div>
          <Button variant="ghost" size="sm" onClick={load} data-testid="admin-ops-refresh">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {Object.entries(byRule).map(([rule, n]) => (
            <Badge key={rule} variant="secondary" className="text-[11px]">{rule} · {n}</Badge>
          ))}
        </div>
        {!alerts.length ? (
          <p className="mt-4 text-xs text-muted-foreground">Nothing flagged. The rules run on every deposit, withdrawal and fill.</p>
        ) : (
          <div className="mt-4 space-y-2.5">
            {alerts.map((a) => (
              <div key={a.id} className="rounded-xl border p-3.5" data-testid={`admin-alert-${a.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={TONE[a.severity] || "secondary"} className="text-[11px]">{a.severity}</Badge>
                  <span className="text-xs font-mono text-muted-foreground">{a.rule}</span>
                  <span className="text-xs text-muted-foreground ms-auto">{a.timestamp?.slice(0, 16).replace("T", " ")}</span>
                </div>
                <p className="mt-1.5 text-sm leading-relaxed">{a.detail}</p>
                <div className="mt-2.5 flex gap-2">
                  {["cleared", "escalated", "reported"].map((r) => (
                    <Button key={r} size="sm" variant={r === "cleared" ? "outline" : "secondary"}
                      onClick={() => resolve(a.id, r)} data-testid={`admin-alert-${r}-${a.id}`}>
                      {r === "cleared" ? "Clear" : r === "escalated" ? "Escalate" : "Report"}
                    </Button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="space-y-4">
        <Card className="p-5 shadow-premium" data-testid="admin-ops-recon">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Scale className="h-4 w-4 text-[color:var(--tharwy-lime)]" />Ledger reconciliation
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">
            Compares every wallet against the double-entry ledger and lists the breaks.
          </p>
          <Button className="mt-3 w-full" variant="secondary" onClick={runRecon}
            disabled={busy === "recon"} data-testid="admin-recon-run">
            {busy === "recon" ? "Reconciling..." : "Run reconciliation"}
          </Button>
          {recon && (
            <p className="mt-3 text-xs numeric" data-testid="admin-recon-result">
              {recon.accounts} accounts · {recon.total_breaks} breaks · {recon.clean ? "clean" : "needs review"}
            </p>
          )}
        </Card>

        <Card className="p-5 shadow-premium" data-testid="admin-ops-export">
          <div className="flex items-center gap-2 text-sm font-medium">
            <FileSpreadsheet className="h-4 w-4 text-[color:var(--tharwy-indigo)]" />Finance &amp; regulator
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">
            The flat journal for your auditor, and the FATCA/CRS account pack.
          </p>
          <div className="mt-3 flex flex-col gap-2">
            <Button variant="secondary" onClick={runExport} disabled={busy === "export"} data-testid="admin-export-run">
              {busy === "export" ? "Building..." : "Check the journal"}
            </Button>
            <Button variant="outline" onClick={openReport} data-testid="admin-regulatory-open">
              Open regulatory report
            </Button>
          </div>
          {exp && (
            <p className="mt-3 text-xs numeric" data-testid="admin-export-result">
              {exp.row_count} postings · {exp.balanced ? "debits equal credits" : "OUT OF BALANCE"}
            </p>
          )}
        </Card>
      </div>
    </div>
  );
};
